import { Component } from '@theme/component';

const STORAGE_KEY = 'product-navigation';

/**
 * @typedef {object} NavigationData
 * @property {string} collectionHandle
 * @property {string[]} products
 * @property {NestedCollection[]} [nestedCollections]
 */

/**
 * @typedef {object} NestedCollection
 * @property {string} handle
 * @property {string[]} products
 */

/**
 * Enhances server-rendered prev/next product navigation with sessionStorage data
 * from the browsed collection, respecting filters, sorting, and nested collections.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} previousLink - The previous product link.
 * @property {HTMLAnchorElement} nextLink - The next product link.
 *
 * @extends {Component<Refs>}
 */
class ProductNavigation extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#updateNavigation();
  }

  #updateNavigation() {
    try {
      const data = this.#getStoredNavigation();
      if (!data) return;

      const { products, handle } = this.#resolveProductList(data);
      if (!products) return;

      const productUrl = this.getAttribute('data-product-url') || window.location.pathname;
      const index = products.indexOf(productUrl);
      if (index === -1) return;

      const param = `collection_handle=${encodeURIComponent(handle)}`;

      this.#setLink(param, this.refs.previousLink, products[index - 1]);
      this.#setLink(param, this.refs.nextLink, products[index + 1]);
    } catch {
      // Graceful degradation — keep Liquid defaults
    }
  }

  /**
   * Reads and parses the stored navigation data from sessionStorage.
   *
   * @returns {NavigationData | null}
   */
  #getStoredNavigation() {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (!data.products || !data.collectionHandle) return null;

    return data;
  }

  /**
   * Resolves the correct product list and collection handle based on
   * the collection_handle URL parameter. Checks nested collections first,
   * then falls back to the main collection.
   *
   * @param {NavigationData} data
   * @returns {{ products: string[] | null, handle: string }}
   */
  #resolveProductList(data) {
    const urlParams = new URLSearchParams(window.location.search);
    const collectionHandle = urlParams.get('collection_handle') || 'all';

    if (data.nestedCollections) {
      const nested = data.nestedCollections.find(
        (nc) => nc.handle === collectionHandle,
      );
      if (nested) {
        return { products: nested.products, handle: nested.handle };
      }
    }

    if (collectionHandle === data.collectionHandle) {
      return { products: data.products, handle: data.collectionHandle };
    }

    return { products: null, handle: collectionHandle };
  }

  /**
   * Sets or hides a navigation link based on the target product URL.
   *
   * @param {string} collectionParam
   * @param {HTMLAnchorElement} [link]
   * @param {string} [productUrl]
   */
  #setLink(collectionParam, link, productUrl) {
    if (!link) return;

    if (productUrl) {
      link.href = `${productUrl}?${collectionParam}`;
      link.classList.remove('hidden');
    } else {
      link.classList.add('hidden');
    }
  }
}

if (!customElements.get('product-navigation')) {
  customElements.define('product-navigation', ProductNavigation);
}
